import request from '@/utils/request'
import type { AxiosResponse } from 'axios'
import {
  ATTENDEE_FILTER_ATTRIBUTE,
  AUTHOR_FILTER_ATTRIBUTE,
  DATE_FILTER_ATTRIBUTE,
  PUBLISHED_FILTER_ATTRIBUTE,
  SEARCH_FILTER_ATTRIBUTE,
  SPACE_FILTER_ATTRIBUTE,
  TYPE_FILTER_ATTRIBUTE,
} from '@/config/search_filter/protocol_filter'
import type { LaravelCollection, LaravelPaginated, LaravelResource } from './types'
import type { Protocol } from '@/models/Protocol'
import type { Contact } from '@/models/Contact'
import type { Task } from '@/models/Task'
import type { ProtocolListRequest } from '@/models/Filters'

interface ListQueryArg {
  page?: number | null
  limit?: number | null
  order?: string | null
  search?: string | null
}

/** GET /api/v1/protocols/{id}/print — PDF blob */
export function printProtocolById(id: string): Promise<AxiosResponse<Blob>> {
  return request({
    url: '/api/v1/protocols/' + id + '/print',
    method: 'get',
    responseType: 'blob',
  })
}

// Comments live on the V2 API — see upsertComment / deleteComment in api/v2/protocols.ts.

/** PUT /api/v1/protocols/{protocol_id}/contacts/{contact_id} — change public flag */
export function changeContactPublic(
  protocol_id: string,
  contact_id: string,
  status: boolean,
): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v1/protocols/' + protocol_id + '/contacts/' + contact_id,
    method: 'put',
    data: { public: status },
  })
}

/** GET /api/v1/protocols/{protocol_id}/files */
export function getFilesForProtocol(
  protocol_id: string,
): Promise<AxiosResponse<LaravelCollection<{ id: string; url: string; name: string }>>> {
  return request({
    url: '/api/v1/protocols/' + protocol_id + '/files',
    method: 'get',
  })
}

/** POST /api/v1/protocols/{protocol_id}/files (multipart) – inline editor image upload */
export function uploadInlineImage(
  protocol_id: string,
  file: File,
): Promise<AxiosResponse<LaravelResource<{ id: string; original_name: string; url: string }>>> {
  const data = new FormData()
  data.append('file', file)
  data.append('name', file.name)
  return request({
    url: '/api/v1/protocols/' + protocol_id + '/files',
    method: 'post',
    data,
  })
}

/** DELETE /api/v1/protocols/{protocol_id}/files/{file_id} */
export function deleteFileForProtocol(
  protocol_id: string,
  file_id: string,
): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v1/protocols/' + protocol_id + '/files/' + file_id,
    method: 'delete',
  })
}

/** DELETE /api/v1/protocols/{protocol_id}/contacts/{contact_id} */
export function removeContactToProtocolLink(
  protocol_id: string,
  contact_id: string,
): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v1/protocols/' + protocol_id + '/contacts/' + contact_id,
    method: 'delete',
  })
}

/** DELETE /api/v1/protocols/{protocol_id}/tasks/{task_id} */
export function removeTaskToProtocolLink(
  protocol_id: string,
  task_id: string,
): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v1/protocols/' + protocol_id + '/tasks/' + task_id,
    method: 'delete',
  })
}

/** GET /api/v1/protocols — paginated, with filters */
export function getAllProtocols(
  data: ProtocolListRequest,
): Promise<AxiosResponse<LaravelPaginated<Protocol>>> {
  const params: string[] = []
  const { listQuery, filterAttributes } = data
  if (listQuery?.page) params.push('page=' + listQuery.page)
  if (listQuery?.limit) params.push('limit=' + listQuery.limit)
  if (listQuery?.order) params.push('order=' + listQuery.order)
  if (filterAttributes) {
    if (filterAttributes.date?.length)
      params.push(DATE_FILTER_ATTRIBUTE + '=' + filterAttributes.date.join(','))
    if (filterAttributes.author?.length)
      params.push(AUTHOR_FILTER_ATTRIBUTE + '=' + filterAttributes.author.join(','))
    if (filterAttributes.published?.length)
      params.push(PUBLISHED_FILTER_ATTRIBUTE + '=' + filterAttributes.published.join(','))
    if (filterAttributes.attendees?.length)
      params.push(ATTENDEE_FILTER_ATTRIBUTE + '=' + filterAttributes.attendees.join(','))
    if (filterAttributes.teams?.length)
      params.push(SPACE_FILTER_ATTRIBUTE + '=' + filterAttributes.teams.join(','))
    // Titles are free text: encoded, unlike the ids around them.
    if (filterAttributes.types?.length)
      params.push(TYPE_FILTER_ATTRIBUTE + '=' + encodeURIComponent(filterAttributes.types.join(',')))
    if (filterAttributes.search) params.push(SEARCH_FILTER_ATTRIBUTE + '=' + filterAttributes.search)
  }
  return request({
    url: '/api/v1/protocols?' + params.join('&'),
    method: 'get',
  })
}

/** GET /api/v1/protocols?filter[search]= — backend matches title and prefix-number */
export function searchProtocolByString(
  search: string = '',
): Promise<AxiosResponse<LaravelCollection<Protocol>>> {
  return request({
    url: '/api/v1/protocols?filter[search]=' + encodeURIComponent(search) + '&limit=10',
    method: 'get',
  })
}

/** POST /api/v1/protocols/{protocol_id}/contacts — link contact to protocol */
export function linkContactToProtocol(
  contact_id: string,
  protocol_id: string,
): Promise<AxiosResponse<LaravelResource<Protocol>>> {
  return request({
    url: '/api/v1/protocols/' + protocol_id + '/contacts',
    method: 'post',
    data: { contact_id },
  })
}

/**
 * POST /api/v1/protocols/{protocol_id}/tasks — link task to protocol.
 *
 * @param source "manual" (default) for an explicit link via the UI,
 *   "mention" for a soft link auto-created from a #-mention.
 */
export function linkTaskToProtocol(
  task_id: string,
  protocol_id: string,
  source?: 'manual' | 'mention',
): Promise<AxiosResponse<LaravelResource<Protocol>>> {
  return request({
    url: '/api/v1/protocols/' + protocol_id + '/tasks',
    method: 'post',
    data: source ? { task_id, source } : { task_id },
  })
}

/** GET /api/v2/protocols/{id} — same payload as v1, which is going away */
export function getProtocolById(
  id: string,
): Promise<AxiosResponse<LaravelResource<Protocol>>> {
  return request({
    url: '/api/v2/protocols/' + id,
    method: 'get',
  })
}

/** GET /api/v1/protocols/{id}/contacts */
export function getContactsForProtocol(
  id: string,
): Promise<AxiosResponse<LaravelCollection<Contact>>> {
  return request({
    url: '/api/v1/protocols/' + id + '/contacts',
    method: 'get',
  })
}

/** GET /api/v1/protocols/{id}/tasks — paginated */
export function getTasksForProtocol(
  id: string,
  listQuery?: ListQueryArg | null,
): Promise<AxiosResponse<LaravelPaginated<Task>>> {
  const params: string[] = []
  if (listQuery?.page) params.push('page=' + listQuery.page)
  if (listQuery?.limit) params.push('limit=' + listQuery.limit)
  if (listQuery?.order) params.push('order=' + listQuery.order)
  if (listQuery?.search) params.push('search=' + encodeURIComponent(listQuery.search))
  return request({
    url: '/api/v1/protocols/' + id + '/tasks?' + params.join('&'),
    method: 'get',
  })
}

/** DELETE /api/v1/protocols/{id} */
export function deleteProtocol(id: string): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v1/protocols/' + id,
    method: 'delete',
  })
}

/** PATCH /api/v1/protocols/{id}/watch */
export function setWatchStatusForProtocol(
  id: string,
  status: 'watch' | 'unwatch',
): Promise<AxiosResponse<LaravelResource<Protocol>>> {
  return request({
    url: '/api/v1/protocols/' + id + '/watch',
    method: 'patch',
    data: { status },
  })
}

/**
 * POST /api/v1/protocols/{id}/viewers
 *
 * @param source "manual" (default) for an explicit add via the UI,
 *   "mention" for a soft add from an @-mention.
 */
export function addViewer(
  id: string,
  user_id: string,
  source?: 'manual' | 'mention',
): Promise<AxiosResponse<LaravelResource<Protocol>>> {
  return request({
    url: '/api/v1/protocols/' + id + '/viewers',
    method: 'post',
    data: source ? { user_id, source } : { user_id },
  })
}

/** DELETE /api/v1/protocols/{id}/viewers/{user_id} */
export function removeViewer(
  id: string,
  user_id: string,
): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v1/protocols/' + id + '/viewers/' + user_id,
    method: 'delete',
  })
}
